'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Copy, X, FileInput, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textDeduplicatorContent } from '../../data/text-deduplicator-content';

export default function TextDeduplicator() {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [preserveCase, setPreserveCase] = useState(true);
    const [removeEmptyLines, setRemoveEmptyLines] = useState(true);
    const [sortLines, setSortLines] = useState(false);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [dedupeBy, setDedupeBy] = useState<'line' | 'word'>('line');
    const { toast } = useToast();

    const processText = useCallback(() => {
        if (!inputText.trim()) {
            setOutputText('');
            return;
        }

        let result = inputText;

        if (dedupeBy === 'line') {
            let lines = result.split('\n');

            if (removeEmptyLines) {
                lines = lines.filter(line => line.trim() !== '');
            }

            const uniqueLines = new Set<string>();
            const processedLines: string[] = [];

            for (const line of lines) {
                const key = preserveCase ? line : line.toLowerCase();
                if (!uniqueLines.has(key)) {
                    uniqueLines.add(key);
                    processedLines.push(line);
                }
            }

            if (sortLines) {
                processedLines.sort((a, b) => {
                    const compareA = preserveCase ? a : a.toLowerCase();
                    const compareB = preserveCase ? b : b.toLowerCase();
                    return sortOrder === 'asc'
                        ? compareA.localeCompare(compareB)
                        : compareB.localeCompare(compareA);
                });
            }

            result = processedLines.join('\n');
        } else {
            const words = result.split(/\s+/);
            const uniqueWords = new Set<string>();
            const processedWords: string[] = [];

            for (const word of words) {
                const key = preserveCase ? word : word.toLowerCase();
                if (!uniqueWords.has(key)) {
                    uniqueWords.add(key);
                    processedWords.push(word);
                }
            }

            if (sortLines) {
                processedWords.sort((a, b) => {
                    const compareA = preserveCase ? a : a.toLowerCase();
                    const compareB = preserveCase ? b : b.toLowerCase();
                    return sortOrder === 'asc'
                        ? compareA.localeCompare(compareB)
                        : compareB.localeCompare(compareA);
                });
            }

            result = processedWords.join(' ');
        }

        setOutputText(result);
    }, [inputText, dedupeBy, preserveCase, removeEmptyLines, sortLines, sortOrder]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInputText(content);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
    };

    const clearText = () => {
        setInputText('');
        setOutputText('');
    };

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <ToolPageIntro title="Text Deduplicator">
                Remove duplicate lines and text with our free online tool. Clean up lists, remove repeated entries, and create unique text content instantly.
            </ToolPageIntro>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Input */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <Label htmlFor="input-text">Input Text</Label>
                        <div className="flex items-center space-x-2">
                            {inputText && (
                                <>
                                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(inputText)}>
                                        <Copy className="h-4 w-4 mr-1" /> Copy
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={clearText}>
                                        <X className="h-4 w-4 mr-1" /> Clear
                                    </Button>
                                </>
                            )}
                            <div className="relative">
                                <input
                                    type="file"
                                    id="file-upload"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    accept=".txt,.md,.js,.jsx,.ts,.tsx,.json,.html,.css,.scss"
                                />
                                <Label
                                    htmlFor="file-upload"
                                    className="cursor-pointer inline-flex items-center px-3 py-1.5 rounded-md hover:bg-accent"
                                >
                                    <FileInput className="h-4 w-4 mr-2" />
                                    Upload File
                                </Label>
                            </div>
                        </div>
                    </div>

                    <Textarea
                        id="input-text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Paste your text here or upload a file..."
                        className="min-h-[300px] font-mono text-sm"
                    />

                    <div className="text-sm text-muted-foreground text-right">
                        {inputText.split(/\s+/).filter(Boolean).length} words • {inputText.length} chars • {inputText.split('\n').length} lines
                    </div>
                </div>

                {/* Output */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <Label htmlFor="output-text">Deduplicated Text</Label>
                        {outputText && (
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(outputText)}>
                                <Copy className="h-4 w-4 mr-1" /> Copy
                            </Button>
                        )}
                    </div>

                    <Textarea
                        id="output-text"
                        value={outputText}
                        readOnly
                        placeholder="Deduplicated text will appear here..."
                        className="min-h-[300px] font-mono text-sm bg-muted/50"
                    />
                </div>
            </div>

            {/* Options */}
            <Card className="mt-8">
                <CardHeader>
                    <CardTitle>Deduplication Options</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="flex items-center space-x-2">
                        <Switch
                            checked={dedupeBy === 'word'}
                            onCheckedChange={(checked) => setDedupeBy(checked ? 'word' : 'line')}
                        />
                        <Label>{dedupeBy === 'line' ? 'Deduplicate by line' : 'Deduplicate by word'}</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch checked={preserveCase} onCheckedChange={setPreserveCase} />
                        <Label>Case sensitive</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch checked={removeEmptyLines} onCheckedChange={setRemoveEmptyLines} />
                        <Label>Remove empty lines</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch checked={sortLines} onCheckedChange={setSortLines} />
                        <Label>Sort {dedupeBy}s</Label>
                    </div>

                    {sortLines && (
                        <div className="flex items-center space-x-2">
                            <Switch
                                checked={sortOrder === 'desc'}
                                onCheckedChange={(checked) => setSortOrder(checked ? 'desc' : 'asc')}
                            />
                            <Label>{sortOrder === 'asc' ? 'A → Z' : 'Z → A'}</Label>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-4">
                <Button variant="outline" onClick={clearText}>
                    <X className="h-4 w-4 mr-2" /> Clear All
                </Button>
                <Button onClick={processText} disabled={!inputText.trim()}>
                    <Check className="h-4 w-4 mr-2" /> Process Text
                </Button>
            </div>

            {/* About Section */}
            <div className="mt-16 bg-muted/50 p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-3">About This Tool</h3>
                <p>
                    Remove duplicate lines or words, clean empty lines, and optionally sort your content.
                    Ideal for logs, contact lists, and data cleanup.
                </p>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={textDeduplicatorContent.title}
                    description={textDeduplicatorContent.description}
                    content={textDeduplicatorContent.content}
                    faqs={textDeduplicatorContent.faqs}
                    relatedTools={textDeduplicatorContent.relatedTools}
                />
            </div>
        </div>
    );
}
